import { useState, useEffect, useCallback, useRef } from "react";
import { api, getWeekStart } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ChevronLeft, ChevronRight, RefreshCw, Plus, ShoppingCart, Trash2, X, Store, Receipt, DollarSign, Download, Ship, Check, Sparkles, Pencil, Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CatalogSuggestions from "@/components/CatalogSuggestions";
import AddReceiptDialog from "@/components/shopping/AddReceiptDialog";
import ImportNotesDialog from "@/components/shopping/ImportNotesDialog";
import GenerateFromPlanDialog from "@/components/shopping/GenerateFromPlanDialog";
import SwipeableRow from "@/components/shopping/SwipeableRow";
import QuickAddBar from "@/components/shopping/QuickAddBar";
import CategoryChips from "@/components/shopping/CategoryChips";
import AIShoppingTab from "@/components/shopping/AIShoppingTab";
import BarcodeScannerDialog from "@/components/BarcodeScannerDialog";
import DuplicateBanner from "@/components/DuplicateBanner";
import { toast } from "sonner";
import { CATEGORIES, CATEGORY_LABELS, PANTRY_TO_SHOPPING } from "@/lib/shoppingConstants";
import { exportShoppingListPdf } from "@/lib/shoppingPdf";

export default function ShoppingList({ servings } = {}) {
  const { user, activeBoat, loadActiveBoat } = useAuth();
  // Per-shift main shopping list: read the user's active shift so each shift
  // keeps its own Hovedliste. Custom lists are shared and ignore this.
  const activeShift = (() => {
    if (user?.preferred_shift === "1" || user?.preferred_shift === "2") return user.preferred_shift;
    try { return localStorage.getItem("pb-active-shift") || "1"; } catch { return "1"; }
  })();
  const isHousehold = activeBoat?.kind === "household";
  const stockLabel = isHousehold ? "hjemme" : "om bord";
  const stockLabelTitle = isHousehold ? "Hjemme" : "Om bord";
  const [weekStart, setWeekStart] = useState(getWeekStart());
  const [shoppingList, setShoppingList] = useState(null);
  const [stores, setStores] = useState([]);
  const [newItemName, setNewItemName] = useState("");
  const [newItemAmount, setNewItemAmount] = useState("");
  const [newItemUnit, setNewItemUnit] = useState("stk");
  const [newItemCategory, setNewItemCategory] = useState("Other");
  // Autocomplete
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [newReceipt, setNewReceipt] = useState({ amount: "", note: "", date: new Date().toISOString().split("T")[0] });
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesText, setNotesText] = useState("");
  const [shoppingSort, setShoppingSort] = useState("default"); // "default" | "alpha"
  const [listTab, setListTab] = useState("main"); // "main" | "ai"
  const [generatingAI, setGeneratingAI] = useState(false);
  const [selectedStore, setSelectedStore] = useState(activeBoat?.preferred_store || "");
  const [checkingOut, setCheckingOut] = useState(false);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [showAddDetails, setShowAddDetails] = useState(false);
  const [estimatingPrices, setEstimatingPrices] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeShoppingCategory, setActiveShoppingCategory] = useState("all");
  const [chipDraggedIdx, setChipDraggedIdx] = useState(null);
  const [chipDragOverIdx, setChipDragOverIdx] = useState(null);
  // Multi-list support
  const [allLists, setAllLists] = useState([]); // metadata for every list this boat owns
  const [activeListId, setActiveListId] = useState(() => localStorage.getItem("sl-active-list") || "main");

  const loadAllLists = useCallback(async () => {
    try {
      const res = await api.listAllShoppingLists(activeShift);
      setAllLists(res.data?.lists || []);
      // If current activeListId disappeared (deleted/renamed), fall back to main
      const exists = (res.data?.lists || []).some((l) => l.list_id === activeListId);
      if (!exists) {
        setActiveListId("main");
        localStorage.setItem("sl-active-list", "main");
      }
    } catch (err) { console.warn("loadAllLists failed", err); }
  }, [activeListId, activeShift]);

  useEffect(() => {
    localStorage.setItem("sl-active-list", activeListId);
  }, [activeListId]);

  const loadData = useCallback(async () => {
    try {
      const [slRes, storesRes] = await Promise.all([
        api.getShoppingList(weekStart, activeListId, activeShift),
        api.getStores(),
      ]);
      setShoppingList(slRes.data);
      setStores(storesRes.data);
      // Notify duplicate banner to refresh after data changes
      window.dispatchEvent(new Event("shopping-dupe-refresh"));
    } catch {
      toast.error("Kunne ikke laste handleliste");
    }
  }, [weekStart, activeListId, activeShift]);

  useEffect(() => { loadData(); loadAllLists(); }, [loadData, loadAllLists]);

  // Reload data etter at pending mutasjonar er synka (offline → online)
  useEffect(() => {
    const h = () => { loadData(); loadAllLists(); };
    window.addEventListener("pb-synced", h);
    return () => window.removeEventListener("pb-synced", h);
  }, [loadData, loadAllLists]);

  const handleCreateList = async () => {
    const name = window.prompt("Navn på ny handleliste (f.eks. Helgemat):");
    if (!name?.trim()) return;
    try {
      const res = await api.createShoppingList(name.trim());
      toast.success(`«${res.data.name}» opprettet`);
      await loadAllLists();
      setActiveListId(res.data.list_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke opprette liste");
    }
  };

  const handleRenameList = async () => {
    const currentList = allLists.find((l) => l.list_id === activeListId);
    const newName = window.prompt("Nytt navn på lista:", currentList?.name || "");
    if (!newName?.trim() || newName.trim() === currentList?.name) return;
    try {
      await api.renameShoppingList(activeListId, newName.trim());
      toast.success("Lista omdøpt");
      await loadAllLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke endre navn");
    }
  };

  const handleDeleteList = async () => {
    if (activeListId === "main") {
      if (!window.confirm("Tømme hele Hovedlista? Alle varer slettes — kan ikke angres.")) return;
      try {
        await api.clearShoppingList(weekStart, "main", activeShift);
        toast.success("Hovedlista tømt");
        await loadData();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Kunne ikke tømme lista");
      }
      return;
    }
    const currentList = allLists.find((l) => l.list_id === activeListId);
    if (!window.confirm(`Slett «${currentList?.name || "lista"}»? Dette kan ikke angres.`)) return;
    try {
      await api.deleteShoppingList(activeListId);
      toast.success("Lista slettet");
      setActiveListId("main");
      await loadAllLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke slette liste");
    }
  };

  const shiftWeek = (dir) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d.toISOString().split("T")[0]);
  };

  const formatWeekRange = () => {
    const start = new Date(weekStart);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    const fmt = (d) => `${d.getDate()}.${d.getMonth() + 1}`;
    return `${fmt(start)} - ${fmt(end)}`;
  };

  const generateAIList = async () => {
    setGeneratingAI(true);
    try {
      const res = await api.generateAIShoppingList(weekStart, activeShift);
      setShoppingList(res.data);
      setListTab("ai");
      const n = (res.data.ai_items || []).length;
      if (n === 0) {
        toast.success("AI-handlelista er tom – alt står allerede i beholdningen!");
      } else {
        toast.success(`AI-handleliste generert: ${n} vare${n === 1 ? "" : "r"} du mangler`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke generere AI-handleliste");
    } finally { setGeneratingAI(false); }
  };

  const moveAIToMain = async () => {
    try {
      const res = await api.moveAIItemsToMain(weekStart, activeShift);
      setShoppingList(res.data);
      setListTab("main");
      toast.success("AI-varer flyttet over til hovedlista");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved flytting");
    }
  };

  const estimatePrices = async () => {
    setEstimatingPrices(true);
    toast.info("AI estimerer priser (gj.snitt + Rema 1000)...");
    try {
      const res = await api.estimateAIItemPrices(weekStart, activeShift);
      const newAiItems = res.data.items || [];
      setShoppingList((sl) => sl ? { ...sl, ai_items: newAiItems } : sl);
      const avg = Math.round(res.data.total_nok || 0);
      const rema = Math.round(res.data.total_rema_nok || 0);
      if (rema > 0 && avg > 0) {
        const saved = avg - rema;
        toast.success(`Gj.snitt: ${avg} kr · Rema 1000: ${rema} kr${saved > 0 ? ` (spar ${saved} kr)` : ""}`);
      } else {
        toast.success(`Estimert totalpris: ${avg} kr`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke estimere priser");
    } finally { setEstimatingPrices(false); }
  };

  const [storeComparison, setStoreComparison] = useState({ stores: [], items_total: 0 });
  const [selectedCompareStore, setSelectedCompareStore] = useState(null);

  const fetchLivePrices = async () => {
    setEstimatingPrices(true);
    toast.info("Henter sanntidspriser fra kassal.app...");
    try {
      const res = await api.shoppingListLivePrices(weekStart, activeShift);
      const newAiItems = res.data.items || [];
      setShoppingList((sl) => sl ? { ...sl, ai_items: newAiItems } : sl);
      setStoreComparison({
        stores: res.data.stores || [],
        items_total: res.data.items_total || newAiItems.length,
      });
      const matched = res.data.matched || 0;
      const total = res.data.total_matched || 0;
      const live = Math.round(res.data.total_live_nok || 0);
      const cheapest = (res.data.stores || [])[0];
      if (cheapest) {
        setSelectedCompareStore(cheapest.store_code);
        toast.success(`Billigst: ${cheapest.store_name} – ${Math.round(cheapest.total_nok)} kr (${res.data.stores.length} butikker sammenlignet)`);
      } else if (matched > 0) {
        toast.success(`Live-pris: ${live} kr · ${matched}/${total} varer matchet`);
      } else {
        toast.info("Ingen live-priser funnet. Kassal dekker ikke alle varer.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke hente live-priser");
    } finally { setEstimatingPrices(false); }
  };

  const addAIItemToMain = async (aiItem) => {
    if (!shoppingList) return;
    // Strip volatile price_live_* fields (they go stale) when moving to main list
    const { price_live_nok, price_live_store, price_live_product, price_live_url, ...cleanedItem } = aiItem;
    const newItem = { ...cleanedItem, id: `ai-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, is_manual: true, checked: false, status: "" };
    const updated = {
      ...shoppingList,
      items: [...(shoppingList.items || []), newItem],
      ai_items: (shoppingList.ai_items || []).filter((i) => i.id !== aiItem.id),
    };
    setShoppingList(updated);
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      toast.success(`${aiItem.name} lagt til i hovedlista`);
    } catch {
      toast.error("Feil ved oppdatering");
      loadData();
    }
  };

  const removeAIItem = async (itemId) => {
    if (!shoppingList) return;
    const updated = {
      ...shoppingList,
      ai_items: (shoppingList.ai_items || []).filter((i) => i.id !== itemId),
    };
    setShoppingList(updated);
    try { await api.updateShoppingList(weekStart, updated, activeListId, activeShift); }
    catch { toast.error("Feil ved oppdatering"); loadData(); }
  };

  const toggleItem = async (itemId) => {
    if (!shoppingList) return;
    const updated = {
      ...shoppingList,
      items: shoppingList.items.map((item) =>
        item.id === itemId ? { ...item, checked: !item.checked } : item
      ),
    };
    setShoppingList(updated);
    try { await api.updateShoppingList(weekStart, updated, activeListId, activeShift); }
    catch { toast.error("Feil ved oppdatering"); }
  };

  const setItemStatus = async (itemId, newStatus) => {
    if (!shoppingList) return;
    const item = shoppingList.items.find((i) => i.id === itemId);
    const isToggleOff = item?.status === newStatus;
    const updated = {
      ...shoppingList,
      items: shoppingList.items.map((i) =>
        i.id === itemId ? { ...i, status: isToggleOff ? "" : newStatus, checked: !isToggleOff && (newStatus === "kjopt" || newStatus === "har_om_bord") } : i
      ),
    };
    setShoppingList(updated);
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      // NOTE: Items marked as "kjopt" or "har_om_bord" are NOT auto-added to
      // pantry. The user wants the receipt scan/confirm flow to be the single
      // source of truth for inventory updates.
    } catch { toast.error("Feil ved oppdatering"); }
  };

  // Barcode-scan handler — if the decoded product matches an existing item on
  // the list, mark it as bought; otherwise pre-fill the quick-add input.
  const handleBarcodeDetected = ({ code, product }) => {
    const name = (product?.name || "").trim();
    if (!shoppingList || !shoppingList.items) {
      if (name) setNewItemName(name);
      return;
    }
    const needle = (name || "").toLowerCase();
    const match = needle
      ? shoppingList.items.find((it) => (it.name || "").toLowerCase().includes(needle) || needle.includes((it.name || "").toLowerCase()))
      : null;
    if (match) {
      setItemStatus(match.id, "kjopt");
      toast.success(`${match.name} markert som kjøpt`);
      return;
    }
    if (name) {
      setNewItemName(name);
      toast.info(`«${name}» klar — trykk Enter for å legge til`);
    } else {
      setNewItemName(`EAN ${code}`);
      toast.info(`Strekkode ${code} — gi varen eit namn og legg til`);
    }
  };

  // ─── Name autocomplete ───
  useEffect(() => {
    const q = newItemName.trim();
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setSuggestLoading(true);
        const res = await api.getPantryNameSuggestions(q);
        if (cancelled) return;
        const items = res.data?.items || [];
        const normalized = q.toLowerCase();
        const filtered = items.filter((it) => it.name.toLowerCase() !== normalized);
        setNameSuggestions(filtered.slice(0, 8));
      } catch {
        if (!cancelled) setNameSuggestions([]);
      } finally {
        if (!cancelled) setSuggestLoading(false);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [newItemName]);

  const selectSuggestion = (s) => {
    setNewItemName(s.name);
    // Map pantry category → shopping category
    const shoppingCat = PANTRY_TO_SHOPPING[s.category] || s.category;
    if (shoppingCat && CATEGORIES.includes(shoppingCat)) setNewItemCategory(shoppingCat);
    setSuggestionsOpen(false);
  };

  const addManualItem = async () => {
    if (!newItemName.trim() || !shoppingList) return;
    // Auto-classify category if user left default "Other"
    let category = newItemCategory;
    if (category === "Other") {
      try {
        const res = await api.classifyShoppingItems([newItemName.trim()]);
        const guessed = res.data?.items?.[0]?.category;
        if (guessed) category = guessed;
      } catch (err) {
        // AI classification is best-effort; keep "Other" on failure
        console.info("AI-kategorisering feilet, bruker Other", err?.message);
      }
    }
    // Auto-fetch live price from Kassal (best-effort, short timeout)
    let livePrice = null;
    let priceFromOtherStore = false;
    try {
      const preferred = (selectedStore || activeBoat?.preferred_store || "").toString();
      const res = await api.kassalQuickPrice(newItemName.trim(), preferred);
      if (res.data?.price_nok) {
        livePrice = { nok: res.data.price_nok, store: res.data.store, product: res.data.product };
        priceFromOtherStore = preferred && !res.data.preferred_store_match;
      }
    } catch (err) {
      console.info("kassalQuickPrice feilet", err?.message);
    }
    const newItem = {
      id: crypto.randomUUID(),
      name: newItemName.trim(),
      amount: newItemAmount.trim(),
      unit: newItemUnit.trim(),
      category: category,
      checked: false,
      status: "",
      is_manual: true,
      ...(livePrice ? {
        price_live_nok: livePrice.nok,
        price_live_store: livePrice.store,
        price_live_product: livePrice.product,
      } : {}),
    };
    const updated = { ...shoppingList, items: [...shoppingList.items, newItem] };
    setShoppingList(updated);
    setNewItemName("");
    setNewItemAmount("");
    setNewItemUnit("stk");
    setNewItemCategory("Other");
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      if (livePrice) {
        if (priceFromOtherStore) {
          toast.success(`Lagt til · ${Math.round(livePrice.nok)} kr (${livePrice.store} — Kassal dekker ikke valgt butikk)`);
        } else {
          toast.success(`Lagt til · ${Math.round(livePrice.nok)} kr (${livePrice.store || "live"})`);
        }
      } else {
        toast.success("Vare lagt til (ingen pris funnet)");
      }
      // Cross-check: warn if this item already exists in pantry
      try {
        const dupRes = await api.getShoppingDuplicates(weekStart, activeShift);
        const inPantry = (dupRes.data?.in_pantry || []).find((p) => p.list_item_id === newItem.id);
        if (inPantry) {
          toast.warning(`Du har alt ${inPantry.pantry_amount || ""} ${inPantry.pantry_unit || ""} ${inPantry.pantry_item_name} i beholdning`.trim(), {
            duration: 10000,
            action: {
              label: "Fjern fra liste",
              onClick: () => removeItem(newItem.id),
            },
          });
        }
      } catch (err) { console.info("dupe-check feilet", err?.message); }
    } catch { toast.error("Feil ved lagring"); }
  };

  const removeItem = async (itemId) => {
    if (!shoppingList) return;
    const updated = { ...shoppingList, items: shoppingList.items.filter((item) => item.id !== itemId) };
    setShoppingList(updated);
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      toast.success("Vare slettet");
    } catch { toast.error("Feil ved sletting"); }
  };

  const updateItemField = async (itemId, field, value) => {
    if (!shoppingList) return;
    const updated = { ...shoppingList, items: shoppingList.items.map((i) => i.id === itemId ? { ...i, [field]: value } : i) };
    setShoppingList(updated);
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
    } catch (err) {
      console.warn("Kunne ikke lagre endring:", err?.message);
    }
  };

  // Debounced "type freely without jank" persistence: keep local state in
  // sync immediately so cursor & input stay smooth, but only POST to the
  // backend 700ms after the user stops typing. Prevents the "blide vibrerer"
  // effect from frequent re-renders + network round-trips on every keystroke.
  const updateItemFieldTimers = useRef({});
  const updateItemFieldDebounced = (itemId, field, value) => {
    if (!shoppingList) return;
    setShoppingList((sl) => sl ? {
      ...sl,
      items: sl.items.map((i) => i.id === itemId ? { ...i, [field]: value } : i),
    } : sl);
    const k = `${itemId}:${field}`;
    if (updateItemFieldTimers.current[k]) clearTimeout(updateItemFieldTimers.current[k]);
    updateItemFieldTimers.current[k] = setTimeout(async () => {
      try {
        setShoppingList((sl) => {
          if (!sl) return sl;
          // Persist the latest state value
          api.updateShoppingList(weekStart, sl, activeListId).catch((err) => {
            console.warn("Auto-lagring feilet:", err?.message);
          });
          return sl;
        });
      } catch (err) { console.warn("Feil ved debounced save:", err?.message); }
      delete updateItemFieldTimers.current[k];
    }, 700);
  };

  const clearChecked = async () => {
    if (!shoppingList) return;
    const updated = { ...shoppingList, items: shoppingList.items.filter((item) => !item.status && !item.checked) };
    setShoppingList(updated);
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      toast.success("Avkryssede varer fjernet");
    } catch { toast.error("Feil ved sletting"); }
  };

  const clearAllItems = async () => {
    if (!shoppingList) return;
    const n = shoppingList.items?.length || 0;
    if (n === 0) return;
    if (!window.confirm(`Slette alle ${n} varer på handlelisten? Dette kan ikke angres.`)) return;
    const updated = { ...shoppingList, items: [] };
    setShoppingList(updated);
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      toast.success(`${n} varer fjernet`);
    } catch { toast.error("Feil ved tømming av lista"); }
  };

  // "Rydd opp" — slå sammen like varer (samme produkt flere ganger) til èin
  // rad med oppdatert antall. Viser bekreftelse før sammenslåing.
  const [cleaningUp, setCleaningUp] = useState(false);
  const cleanupDuplicates = async () => {
    if (!shoppingList || cleaningUp) return;
    setCleaningUp(true);
    try {
      // 1) Deterministisk søk (samme normaliserte navn)
      const detRes = await api.getShoppingDuplicates(weekStart, activeShift);
      const detGroups = (detRes.data?.groups || []);
      // 2) AI-søk (kryss-merke / synonymer)
      let aiGroups = [];
      try {
        const aiRes = await api.aiFindShoppingDuplicates(weekStart);
        aiGroups = (aiRes.data?.groups || []);
      } catch (err) { console.info("AI-dup feilet", err?.message); }

      if (detGroups.length === 0 && aiGroups.length === 0) {
        toast.success("Ingen like varer å slå sammen — lista ser ryddig ut");
        setCleaningUp(false);
        return;
      }

      // Bygg bekreftelses-melding
      const lines = [];
      detGroups.forEach((g) => {
        const names = (g.items || []).map((i) => i.name).join(", ");
        lines.push(`• ${g.name} (${g.count} stk): ${names}`);
      });
      aiGroups.forEach((g) => {
        const names = (g.items || []).map((i) => i.name).join(", ");
        lines.push(`• ${g.name} (${g.count} merker): ${names}`);
      });
      const totalGroups = detGroups.length + aiGroups.length;
      const removed = [...detGroups, ...aiGroups].reduce((s, g) => s + Math.max(0, (g.count || 0) - 1), 0);
      const msg = `Slå sammen ${totalGroups} grupper og fjerne ${removed} duplikat-rader?\n\n${lines.slice(0, 12).join("\n")}${lines.length > 12 ? `\n…og ${lines.length - 12} til` : ""}\n\nAntall for hver vare summeres automatisk.`;
      if (!window.confirm(msg)) { setCleaningUp(false); return; }

      let mergedTotal = 0;
      let removedTotal = 0;
      // 1) Slå sammen deterministiske grupper (alle på èin kall)
      if (detGroups.length > 0) {
        try {
          const res = await api.mergeShoppingDuplicates(weekStart, { keys: detGroups.map((g) => g.key) }, activeShift);
          mergedTotal += res.data?.merged_groups || 0;
          removedTotal += res.data?.removed_items ?? res.data?.deleted_items ?? 0;
        } catch (err) { console.warn("Deterministic merge feilet", err?.message); }
      }
      // 2) Slå sammen AI-grupper èin for èin (krev item_ids per gruppe)
      for (let i = 0; i < aiGroups.length; i++) {
        const g = aiGroups[i];
        const itemIds = (g.items || []).map((it) => it.id);
        try {
          const res = await api.mergeShoppingDuplicates(weekStart, { keys: null, item_ids: [itemIds], name_overrides: { "explicit:0": g.name } }, activeShift);
          mergedTotal += res.data?.merged_groups || 0;
          removedTotal += res.data?.removed_items ?? res.data?.deleted_items ?? 0;
        } catch (err) { console.warn("AI merge feilet", err?.message); }
      }

      toast.success(`Slo sammen ${mergedTotal} grupper · fjernet ${removedTotal} duplikat-rader`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke rydde opp");
    } finally {
      setCleaningUp(false);
    }
  };

  // ─── Multi-line bulk add (paste several items separated by newline / comma) ───
  const addBulkItems = async (raw) => {
    const lines = raw
      .split(/[\n,;]+/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0)
      .slice(0, 50);
    if (lines.length === 0 || !shoppingList) return;

    // Quick category guess for all in one AI call
    let categories = {};
    try {
      const res = await api.classifyShoppingItems(lines);
      for (const it of (res.data?.items || [])) {
        if (it.name && it.category) categories[it.name.toLowerCase()] = it.category;
      }
    } catch (err) { console.info("AI-kategorisering feilet", err?.message); }

    // Fetch prices in parallel (best-effort)
    const store = (selectedStore || activeBoat?.preferred_store || "").toString();
    const priceFetch = await Promise.all(
      lines.map(async (n) => {
        try {
          const r = await api.kassalQuickPrice(n, store);
          return r.data?.price_nok ? { name: n, ...r.data } : null;
        } catch { return null; }
      }),
    );
    const priceMap = Object.fromEntries(priceFetch.filter(Boolean).map((p) => [p.name.toLowerCase(), p]));

    const newItems = lines.map((name) => {
      const lp = priceMap[name.toLowerCase()];
      return {
        id: crypto.randomUUID(),
        name,
        amount: "",
        unit: "stk",
        category: categories[name.toLowerCase()] || "Other",
        checked: false,
        status: "",
        is_manual: true,
        ...(lp ? {
          price_live_nok: lp.price_nok,
          price_live_store: lp.store,
          price_live_product: lp.product,
        } : {}),
      };
    });
    const updated = { ...shoppingList, items: [...shoppingList.items, ...newItems] };
    setShoppingList(updated);
    setNewItemName("");
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      const withPrice = newItems.filter((i) => typeof i.price_live_nok === "number").length;
      toast.success(`Lagt til ${newItems.length} varer · ${withPrice} med pris`);
    } catch { toast.error("Feil ved lagring"); }
  };

  const handleQuickAddSubmit = () => {
    const txt = newItemName.trim();
    if (!txt) return;
    if (/[\n,;]/.test(txt)) {
      addBulkItems(txt);
    } else {
      addManualItem();
    }
  };

  // ─── Fetch prices for all items missing one (uses current selectedStore) ───
  const fetchAllPrices = async () => {
    if (!shoppingList?.items?.length) {
      toast.error("Handlelisten er tom");
      return;
    }
    const needsPrice = shoppingList.items.filter((i) => i.name && typeof i.price_live_nok !== "number");
    if (needsPrice.length === 0) {
      toast.success("Alle varer har allerede pris ✓");
      return;
    }
    setRefreshingPrices(true);
    toast.info(`Henter priser for ${needsPrice.length} varer…`);
    try {
      const store = selectedStore || activeBoat?.preferred_store || "";
      const updatedItems = await Promise.all(
        shoppingList.items.map(async (it) => {
          if (!it.name || typeof it.price_live_nok === "number") return it;
          try {
            const res = await api.kassalQuickPrice(it.name, store);
            if (res.data?.price_nok) {
              return {
                ...it,
                price_live_nok: res.data.price_nok,
                price_live_store: res.data.store,
                price_live_product: res.data.product,
              };
            }
          } catch { /* ignore individual failures */ }
          return it;
        }),
      );
      const updated = { ...shoppingList, items: updatedItems };
      setShoppingList(updated);
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      const matched = updatedItems.filter((i) => typeof i.price_live_nok === "number").length;
      const total = updatedItems.length;
      toast.success(`Priser oppdatert · ${matched}/${total} varer har nå pris`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke hente priser");
    } finally {
      setRefreshingPrices(false);
    }
  };

  // ─── Store ───
  const selectStore = async (storeName) => {
    if (!shoppingList) return;
    const updated = { ...shoppingList, store: storeName };
    setShoppingList(updated);
    try { await api.updateShoppingList(weekStart, updated, activeListId, activeShift); }
    catch { toast.error("Feil ved lagring"); }
  };

  // ─── Store selector (affects live prices for newly added items + refreshes existing prices) ───
  const handleStoreChange = async (newStoreCode) => {
    setSelectedStore(newStoreCode);
    // Persist to boat so other pages (budget widget, live-prices) see it
    if (activeBoat?.id) {
      try { await api.updateBoat(activeBoat.id, { preferred_store: newStoreCode }); }
      catch (err) { console.info("kunne ikke lagre preferred_store", err?.message); }
    }
    // Also persist on the shopping list itself so receipts/checkout link to it
    if (shoppingList && newStoreCode) {
      try {
        const updated = { ...shoppingList, store: newStoreCode };
        setShoppingList(updated);
        await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      } catch { /* non-fatal */ }
    }
    // Refresh live prices for main-list items that already exist
    if (!shoppingList?.items?.length) {
      toast.success(`Butikk: ${newStoreCode}`);
      return;
    }
    setRefreshingPrices(true);
    try {
      const updatedItems = await Promise.all(
        shoppingList.items.map(async (it) => {
          if (!it.name) return it;
          try {
            const res = await api.kassalQuickPrice(it.name, newStoreCode);
            if (res.data?.price_nok) {
              return {
                ...it,
                price_live_nok: res.data.price_nok,
                price_live_store: res.data.store,
                price_live_product: res.data.product,
              };
            }
          } catch { /* ignore individual failures */ }
          return it;
        }),
      );
      const updated = { ...shoppingList, items: updatedItems };
      setShoppingList(updated);
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      const matched = updatedItems.filter((i) => i.price_live_nok).length;
      toast.success(`Priser oppdatert for ${newStoreCode} · ${matched}/${updatedItems.length} matchet`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke oppdatere priser");
    } finally {
      setRefreshingPrices(false);
    }
  };

  // ─── Checkout: lock in bought items, create receipt, update budget ───
  const checkoutShopping = async () => {
    if (!shoppingList) return;
    const bought = (shoppingList.items || []).filter((i) => i.status === "kjopt" || i.checked);
    const aiBought = (shoppingList.ai_items || []).filter((i) => i.status === "kjopt" || i.checked);
    const total = [...bought, ...aiBought].length;
    if (total === 0) {
      toast.error("Marker varer som kjøpt først (trykk '✓ Kjøpt' på hver vare)");
      return;
    }
    const estNok = [...bought, ...aiBought].reduce((sum, it) => {
      const p = Number(it.price_live_nok || it.price_nok || 0);
      const qty = Number(it.amount) || 1;
      return sum + (p * qty);
    }, 0);
    if (!window.confirm(`Fullføre handletur? ${total} varer · ca ${Math.round(estNok)} kr\n\nBeløpet blir lagt til månedens forbruk.`)) return;
    setCheckingOut(true);
    try {
      const res = await api.shoppingCheckout(weekStart, selectedStore, activeShift);
      toast.success(`Lagret · ${Math.round(res.data.total_nok)} kr lagt til budsjettet (${res.data.item_count} varer)`);
      // Reload to pick up the new receipt status; do not auto-clear items so user can see history
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout feilet");
    } finally {
      setCheckingOut(false);
    }
  };

  // ─── Import from notes ───
  const importFromNotes = async () => {
    if (!notesText.trim() || !shoppingList) return;
    const lines = notesText.split("\n").map((l) => l.trim()).filter(Boolean);
    const UNITS = "dl|l|kg|g|stk|pk|ss|ts|boks|pose|fl";
    const parsed = lines.map((line) => {
      // Try "2 dl melk" or "2 melk"  (amount first)
      let m = line.match(new RegExp(`^(\\d+[\\.,]?\\d*)\\s*(${UNITS})?\\s+(.+)$`, "i"));
      if (m) {
        return { id: crypto.randomUUID(), name: m[3].trim(), amount: m[1], unit: m[2] || "stk", category: "Other", checked: false, status: "", is_manual: true };
      }
      // Try "melk 2 dl" or "melk 2"  (name first)
      m = line.match(new RegExp(`^(.+?)\\s+(\\d+[\\.,]?\\d*)\\s*(${UNITS})?$`, "i"));
      if (m) {
        return { id: crypto.randomUUID(), name: m[1].trim(), amount: m[2], unit: m[3] || "stk", category: "Other", checked: false, status: "", is_manual: true };
      }
      // Fallback: plain name
      const cleaned = line.replace(/^[-*•·\d.)\s]+/, "").trim();
      return { id: crypto.randomUUID(), name: cleaned, amount: "1", unit: "stk", category: "Other", checked: false, status: "", is_manual: true };
    }).filter((i) => i.name);
    // Auto-classify via backend (pantry AI logic mapped to shopping categories)
    try {
      const res = await api.classifyShoppingItems(parsed.map((i) => i.name));
      const catByName = {};
      (res.data?.items || []).forEach((c) => { catByName[c.name.toLowerCase()] = c.category; });
      parsed.forEach((item) => {
        const suggested = catByName[item.name.toLowerCase()];
        if (suggested) item.category = suggested;
      });
    } catch (err) {
      // Silent fallback – items keep "Other" so user still sees them added
      console.info("Bulk-kategorisering feilet:", err?.message);
    }
    const updated = { ...shoppingList, items: [...(shoppingList.items || []), ...parsed] };
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      setShoppingList(updated);
      setNotesOpen(false);
      setNotesText("");
      toast.success(`${parsed.length} varer importert og kategorisert!`);
    } catch { toast.error("Feil ved import"); }
  };

  // ─── AI-categorize all items ───
  const aiCategorizeAll = async () => {
    if (!shoppingList?.items?.length) return;
    toast.loading("AI kategoriserer varene...", { id: "ai-cat" });
    try {
      const names = shoppingList.items.map((i) => i.name);
      const res = await api.classifyShoppingItems(names);
      const catByName = {};
      (res.data?.items || []).forEach((c) => { catByName[c.name.toLowerCase()] = c.category; });
      let changed = 0;
      const newItems = shoppingList.items.map((item) => {
        const suggested = catByName[item.name.toLowerCase()];
        if (suggested && suggested !== item.category) {
          changed += 1;
          return { ...item, category: suggested };
        }
        return item;
      });
      const updated = { ...shoppingList, items: newItems };
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      setShoppingList(updated);
      if (changed === 0) toast.success(`Alle varer har allerede riktig kategori (${names.length} varer)`, { id: "ai-cat" });
      else toast.success(`${changed} av ${names.length} varer fikk oppdatert kategori`, { id: "ai-cat" });
    } catch {
      toast.error("Feil ved AI-kategorisering", { id: "ai-cat" });
    }
  };


  // ─── Receipts ───
  const addReceipt = async () => {
    if (!newReceipt.amount || !shoppingList) return;
    const receipt = {
      id: crypto.randomUUID(),
      store: shoppingList.store || "Ukjent butikk",
      amount: parseFloat(newReceipt.amount),
      date: newReceipt.date,
      note: newReceipt.note,
    };
    const updated = { ...shoppingList, receipts: [...(shoppingList.receipts || []), receipt] };
    setShoppingList(updated);
    setReceiptDialogOpen(false);
    setNewReceipt({ amount: "", note: "", date: new Date().toISOString().split("T")[0] });
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      toast.success("Kvittering lagt til");
    } catch { toast.error("Feil ved lagring"); }
  };

  const removeReceipt = async (receiptId) => {
    if (!shoppingList) return;
    const updated = { ...shoppingList, receipts: (shoppingList.receipts || []).filter((r) => r.id !== receiptId) };
    setShoppingList(updated);
    try {
      await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
      toast.success("Kvittering slettet");
    } catch { toast.error("Feil ved sletting"); }
  };

  const groupedItems = (() => {
    let allItems = shoppingList?.items || [];
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      allItems = allItems.filter((it) => (it.name || "").toLowerCase().includes(q) || (it.note || "").toLowerCase().includes(q));
    }
    // Group by shopping-category; map any Norwegian pantry category to its shopping equivalent first.
    const grouped = {};
    for (const it of allItems) {
      const raw = it.category || "Other";
      const cat = CATEGORIES.includes(raw) ? raw : (PANTRY_TO_SHOPPING[raw] || "Other");
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(it);
    }
    // Apply saved chip order if present, fall back to CATEGORIES default order
    const savedOrder = activeBoat?.shopping_category_order || [];
    const orderedCats = savedOrder.length > 0
      ? [...savedOrder.filter((c) => grouped[c]?.length), ...CATEGORIES.filter((c) => grouped[c]?.length && !savedOrder.includes(c))]
      : CATEGORIES.filter((c) => grouped[c]?.length);
    const ordered = {};
    for (const cat of orderedCats) {
      // Filter by active category chip (ignored when searching so search results stay broad)
      if (activeShoppingCategory !== "all" && cat !== activeShoppingCategory && !searchQuery.trim()) continue;
      ordered[cat] = shoppingSort === "alpha"
        ? [...grouped[cat]].sort((a, b) => (a.name || "").localeCompare(b.name || "", "nb", { sensitivity: "base" }))
        : grouped[cat];
    }
    return ordered;
  })();

  // Counts per category (across ALL items, not filtered by active chip) so the
  // chip badges always reflect the underlying list.
  const categoryCounts = (() => {
    const counts = {};
    for (const it of (shoppingList?.items || [])) {
      const raw = it.category || "Other";
      const cat = CATEGORIES.includes(raw) ? raw : (PANTRY_TO_SHOPPING[raw] || "Other");
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  })();
  const savedShoppingOrder = activeBoat?.shopping_category_order || [];
  const baseCategoriesWithItems = CATEGORIES.filter((c) => categoryCounts[c] > 0);
  // Apply saved order if available: known categories first, then any new ones
  const categoriesWithItems = (() => {
    if (savedShoppingOrder.length === 0) return baseCategoriesWithItems;
    const setHasItems = new Set(baseCategoriesWithItems);
    const ordered = savedShoppingOrder.filter((c) => setHasItems.has(c));
    const remaining = baseCategoriesWithItems.filter((c) => !ordered.includes(c));
    return [...ordered, ...remaining];
  })();

  const handleShoppingChipDrop = async (targetIdx) => {
    if (chipDraggedIdx === null || chipDraggedIdx === targetIdx) return;
    const reordered = [...categoriesWithItems];
    const [moved] = reordered.splice(chipDraggedIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    setChipDraggedIdx(null);
    setChipDragOverIdx(null);
    if (!activeBoat?.id) return;
    try {
      await api.updateBoatShoppingCategoryOrder(activeBoat.id, reordered);
      await loadActiveBoat?.();
      toast.success("Rekkefølge lagret");
    } catch {
      toast.error("Kunne ikke lagre rekkefølge");
    }
  };

  const totalItems = shoppingList?.items?.length || 0;
  const checkedItems = shoppingList?.items?.filter((i) => i.checked || i.status === "kjopt" || i.status === "har_om_bord").length || 0;
  const harOmBordCount = shoppingList?.items?.filter((i) => i.status === "har_om_bord").length || 0;
  const kjoptCount = shoppingList?.items?.filter((i) => i.status === "kjopt").length || 0;
  const needCount = totalItems - harOmBordCount - kjoptCount;
  const totalSpent = (shoppingList?.receipts || []).reduce((sum, r) => sum + r.amount, 0);

  const exportPdf = () => exportShoppingListPdf({
    shoppingList,
    weekStart,
    weekRangeText: formatWeekRange(),
    stockLabelTitle,
    totalSpent,
  });

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto" data-testid="shopping-list-page">
      {/* List selector */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="list-selector-wrapper">
        <Select value={activeListId} onValueChange={setActiveListId}>
          <SelectTrigger className="w-44 sm:w-56 bg-white" data-testid="list-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allLists.map((l) => (
              <SelectItem key={l.list_id} value={l.list_id}>
                {l.name}{l.item_count > 0 ? ` · ${l.item_count}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {activeListId === "main" && (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded bg-stone-900 text-white text-[10px] font-bold tracking-wide"
            data-testid="shopping-shift-indicator"
            title={`Hovedlista er per-skift — du ser Skift ${activeShift}`}
          >
            S{activeShift}
          </span>
        )}
        <button onClick={handleCreateList} className="inline-flex items-center gap-1 rounded-md bg-navy-800 hover:bg-navy-900 px-2.5 py-2 text-xs font-semibold text-white" data-testid="create-list-btn" title="Ny handleliste">
          <Plus className="h-3.5 w-3.5" /> Ny
        </button>
        {activeListId !== "main" && (
          <button onClick={handleRenameList} className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50" data-testid="rename-list-btn" title="Endre navn">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        <button onClick={handleDeleteList} className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-2 text-xs font-medium text-red-600 hover:bg-red-50" data-testid="delete-list-btn" title={activeListId === "main" ? "Tøm hovedlista" : "Slett liste"}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <DuplicateBanner
        fetchFn={() => api.getShoppingDuplicates(weekStart, activeShift)}
        mergeFn={(keys, itemIds, nameOverrides, categoryOverrides) =>
          api.mergeShoppingDuplicates(
            weekStart,
            {
              keys,
              item_ids: itemIds,
              name_overrides: nameOverrides,
              category_overrides: categoryOverrides,
            },
            activeShift,
          )
        }
        aiFetchFn={() => api.aiFindShoppingDuplicates(weekStart)}
        rejectFn={api.rejectShoppingDuplicate}
        title="duplikater i handlelisten"
        onMerged={loadData}
        onRemoveInPantry={async (p) => {
          await removeItem(p.list_item_id);
        }}
        testIdPrefix="shopping-dupe"
      />
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Handleliste
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {totalItems > 0 ? (
              <span>
                {needCount > 0 && <span className="text-stone-600">{needCount} trenger</span>}
                {harOmBordCount > 0 && <span className="text-sky-600 ml-2">{harOmBordCount} har {stockLabel}</span>}
                {kjoptCount > 0 && <span className="text-seawater-600 ml-2">{kjoptCount} kjøpt</span>}
              </span>
            ) : "Ingen varer ennå"}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {(kjoptCount > 0 || harOmBordCount > 0) && (
            <button onClick={clearChecked} className="inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors gap-2" data-testid="clear-checked-btn">
              <X className="h-4 w-4" />
              Fjern avhukede ({checkedItems})
            </button>
          )}
          {totalItems > 0 && (
            <button onClick={cleanupDuplicates} disabled={cleaningUp} className="inline-flex items-center justify-center rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 transition-colors gap-2 disabled:opacity-60" data-testid="cleanup-duplicates-btn" title="Slå sammen like varer (AI) og oppdatèr antall">
              <Sparkles className="h-4 w-4" /> {cleaningUp ? "Rydder…" : "Rydd opp like varer"}
            </button>
          )}
          {totalItems > 0 && (
            <button onClick={clearAllItems} className="inline-flex items-center justify-center rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 transition-colors gap-2" data-testid="clear-all-shopping-btn" title="Slett alle varer på handlelisten">
              <Trash2 className="h-4 w-4" />
              Tøm hele lista
            </button>
          )}
          <button onClick={exportPdf} className="inline-flex items-center justify-center rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors gap-2" data-testid="export-pdf-btn">
            <Download className="h-4 w-4" /> Eksporter PDF
          </button>
          {totalItems > 0 && (
            <button onClick={aiCategorizeAll} className="inline-flex items-center justify-center rounded-md bg-seawater-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-800 transition-colors gap-2 shadow-sm ring-1 ring-seawater-500" data-testid="ai-categorize-shopping-btn" title="AI grupperer alle varene etter kategori">
              <Sparkles className="h-4 w-4" /> AI-kategoriser
            </button>
          )}
          <button onClick={() => setGenerateDialogOpen(true)} disabled={generating} className="inline-flex items-center justify-center rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors gap-2 disabled:opacity-50" data-testid="generate-shopping-list-btn">
            <RefreshCw className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} />
            Generer fra ukeplan
          </button>
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" title="Lista lagres til du sletter den manuelt">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Vedvarende liste</span>
            <span className="text-xs text-emerald-700/80">· endres ikke ved ukeskifte</span>
          </div>
        </div>
      </div>

      {/* Store selector + Receipt summary bar */}
      <div className="mb-6">
        {/* Receipt summary */}
        <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid="receipt-summary-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold flex items-center gap-2">
              <Receipt className="h-3.5 w-3.5" />
              Kvitteringer
            </h3>
            <button onClick={() => setReceiptDialogOpen(true)} className="text-xs text-navy-800 hover:text-navy-800 font-medium transition-colors" data-testid="add-receipt-btn">
              + Legg til kvittering
            </button>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="total-spent">
              {totalSpent.toFixed(2)} kr
            </span>
            <span className="text-xs text-stone-400">
              totalt ({(shoppingList?.receipts || []).length} kvitteringer)
            </span>
          </div>
        </div>
      </div>

      {/* Receipts list */}
      {(shoppingList?.receipts || []).length > 0 && (
        <div className="mb-6" data-testid="receipts-list">
          <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3">Kvitteringshistorikk</h3>
          <div className="bg-white border border-stone-100 rounded-xl divide-y divide-stone-100">
            {(shoppingList?.receipts || []).map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-3 group" data-testid={`receipt-item-${r.id}`}>
                <div className="w-8 h-8 rounded-full bg-sailcloth-100 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="h-4 w-4 text-navy-800" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-stone-800">{r.amount.toFixed(2)} kr</span>
                    <span className="text-xs text-stone-400">{r.store}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-stone-400">{r.date}</span>
                    {r.note && <span className="text-xs text-stone-500">– {r.note}</span>}
                  </div>
                </div>
                <button onClick={() => removeReceipt(r.id)} className="p-1.5 hover:bg-red-50 rounded-md text-stone-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100" data-testid={`remove-receipt-${r.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress bar */}
      {totalItems > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-stone-400 font-medium">{checkedItems} av {totalItems} håndtert</span>
            <button
              onClick={() => setShoppingSort(shoppingSort === "alpha" ? "default" : "alpha")}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                shoppingSort === "alpha"
                  ? "bg-navy-800 text-white border-navy-800"
                  : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
              }`}
              data-testid="shopping-sort-alpha"
              title="Sorter varene alfabetisk A-Å innenfor hver kategori"
            >
              A-Å
            </button>
          </div>
          <div className="w-full bg-stone-100 rounded-full h-2">
            <div className="bg-navy-800 h-2 rounded-full transition-all duration-500" style={{ width: `${(checkedItems / totalItems) * 100}%` }} data-testid="shopping-progress" />
          </div>
        </div>
      )}

      {/* ─── Live totalsum banner ─── */}
      {(() => {
        const allItems = [...(shoppingList?.items || []), ...(shoppingList?.ai_items || [])];
        const itemsWithPrice = allItems.filter((i) => typeof i.price_live_nok === "number");
        if (itemsWithPrice.length === 0) return null;
        const totalAll = itemsWithPrice.reduce((sum, i) => {
          const qty = Number(i.amount) || 1;
          return sum + (Number(i.price_live_nok) || 0) * qty;
        }, 0);
        const boughtItems = itemsWithPrice.filter((i) => i.status === "kjopt" || i.checked);
        const totalBought = boughtItems.reduce((sum, i) => {
          const qty = Number(i.amount) || 1;
          return sum + (Number(i.price_live_nok) || 0) * qty;
        }, 0);
        const totalRemaining = totalAll - totalBought;
        return (
          <div className="mb-4 rounded-xl border border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50 p-3" data-testid="shopping-total-banner">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-teal-700 font-bold">Totalsum</div>
                <div className="text-2xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {Math.round(totalAll)} <span className="text-base font-medium text-stone-500">kr</span>
                </div>
                <div className="text-[10px] text-stone-500 mt-0.5">
                  {itemsWithPrice.length} av {allItems.length} varer har pris
                </div>
              </div>
              <div className="flex gap-3 text-right">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Kjøpt</div>
                  <div className="text-base font-bold text-emerald-800">{Math.round(totalBought)} kr</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Igjen</div>
                  <div className="text-base font-bold text-amber-800">{Math.round(totalRemaining)} kr</div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ─── Store selector + Checkout ─── */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="store-selector-bar">
        <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Butikk:</label>
        <select
          value={selectedStore}
          onChange={(e) => handleStoreChange(e.target.value)}
          disabled={refreshingPrices}
          className="rounded-md border border-stone-200 bg-white px-2 py-1.5 text-xs font-medium text-stone-700 focus:border-teal-500 focus:outline-none disabled:opacity-50 flex-shrink min-w-0"
          data-testid="shopping-store-select"
        >
          <option value="">Billigste</option>
          <optgroup label="Live priser (Kassal)">
            <option value="coop">Coop</option>
            <option value="meny">Meny</option>
            <option value="joker">Joker</option>
            <option value="spar">Spar</option>
            <option value="oda">Oda</option>
          </optgroup>
          <optgroup label="AI-estimerte priser">
            <option value="rema">REMA 1000</option>
            <option value="kiwi">Kiwi</option>
            <option value="bunnpris">Bunnpris</option>
          </optgroup>
        </select>
        {refreshingPrices && <span className="hidden sm:inline text-[11px] text-teal-700 font-medium">Oppdaterer…</span>}
        <button
          onClick={fetchAllPrices}
          disabled={refreshingPrices || !(shoppingList?.items?.length)}
          className="inline-flex items-center gap-1.5 rounded-md border border-teal-300 bg-teal-50 px-2 sm:px-3 py-1.5 text-xs font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="fetch-all-prices-btn"
          title="Hent live-pris for alle varer som mangler pris"
        >
          <DollarSign className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Hent priser</span>
        </button>
        <button
          onClick={checkoutShopping}
          disabled={checkingOut || (shoppingList?.items || []).filter((i) => i.status === "kjopt").length === 0}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="shopping-checkout-btn"
          title="Fullfør handletur: lagrer totalbeløpet i månedens forbruk"
        >
          {checkingOut ? "Lagrer…" : (
            <>
              <span className="sm:hidden">✓ Fullfør</span>
              <span className="hidden sm:inline">✓ Fullfør handletur</span>
            </>
          )}
        </button>
      </div>

      {/* Tab switcher: Main vs AI list */}
      <div className="flex gap-2 mb-4 print-hide" data-testid="shopping-tabs">
        <button
          onClick={() => setListTab("main")}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors border ${
            listTab === "main" ? "bg-navy-800 text-white border-navy-800" : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
          }`}
          data-testid="tab-main"
        >
          <ShoppingCart className="h-4 w-4" /> Handleliste
          {(shoppingList?.items?.length || 0) > 0 && <span className="ml-1 text-[10px] font-bold opacity-70">{shoppingList.items.length}</span>}
        </button>
        <button
          onClick={() => setListTab("ai")}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors border ${
            listTab === "ai" ? "bg-orange-600 text-white border-orange-600" : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
          }`}
          data-testid="tab-ai"
        >
          <Sparkles className="h-4 w-4" /> AI Handleliste
          {(shoppingList?.ai_items?.length || 0) > 0 && <span className="ml-1 text-[10px] font-bold opacity-70">{shoppingList.ai_items.length}</span>}
        </button>
      </div>

      {/* Category chip tabs (only on main list, hidden when searching) */}
      {listTab === "main" && !searchQuery.trim() && (
        <CategoryChips
          items={shoppingList?.items}
          categories={categoriesWithItems}
          categoryCounts={categoryCounts}
          activeCategory={activeShoppingCategory}
          onSelectCategory={setActiveShoppingCategory}
          chipDraggedIdx={chipDraggedIdx}
          chipDragOverIdx={chipDragOverIdx}
          setChipDraggedIdx={setChipDraggedIdx}
          setChipDragOverIdx={setChipDragOverIdx}
          onChipDrop={handleShoppingChipDrop}
        />
      )}

      {/* Search bar (only on main list) */}
      {listTab === "main" && (
        <div className="relative mb-3 print-hide" data-testid="shopping-search-wrapper">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Søk i handlelisten (navn eller notat)…"
            className="w-full rounded-lg border border-stone-200 bg-white pl-9 pr-9 py-2 text-sm text-stone-700 placeholder:text-stone-400 focus:border-navy-800 focus:outline-none"
            data-testid="shopping-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-stone-100 text-stone-400 hover:text-stone-600"
              data-testid="shopping-search-clear"
              title="Tøm søk"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* AI list view */}
      {listTab === "ai" && (
        <AIShoppingTab
          shoppingList={shoppingList}
          isHousehold={isHousehold}
          generatingAI={generatingAI}
          estimatingPrices={estimatingPrices}
          storeComparison={storeComparison}
          selectedCompareStore={selectedCompareStore}
          setSelectedCompareStore={setSelectedCompareStore}
          onGenerateAIList={generateAIList}
          onEstimatePrices={estimatePrices}
          onFetchLivePrices={fetchLivePrices}
          onMoveAIToMain={moveAIToMain}
          onAddAIItemToMain={addAIItemToMain}
          onRemoveAIItem={removeAIItem}
        />
      )}

      {/* Main shopping items by category (hidden when AI tab active) */}
      {listTab === "main" && searchQuery.trim() && Object.keys(groupedItems).length > 0 && (
        <div className="mb-2 flex items-center justify-between gap-2" data-testid="shopping-search-results-header">
          <h3 className="text-xs uppercase tracking-[0.2em] text-navy-700 font-semibold">
            Søkeresultat for «{searchQuery.trim()}»
          </h3>
          <span className="text-xs text-stone-500">
            {Object.values(groupedItems).reduce((s, arr) => s + arr.length, 0)} treff
          </span>
        </div>
      )}
      {listTab === "main" && (Object.keys(groupedItems).length === 0 ? (
        <div data-testid="empty-shopping-list">
          <div className="text-center py-12">
            <ShoppingCart className="h-12 w-12 mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {searchQuery.trim() ? `Ingen treff på «${searchQuery.trim()}»` : "Handlelisten er tom"}
            </h3>
            <p className="text-sm text-stone-400 mt-1">
              {searchQuery.trim() ? "Prøv et annet søkeord" : "Generer fra ukeplanen eller legg til varer manuelt nedenfor"}
            </p>
          </div>
          {!searchQuery.trim() && <CatalogSuggestions onAdded={loadData} />}
        </div>
      ) : (
        <div className="space-y-6" data-testid="shopping-items">
          {Object.entries(groupedItems).map(([category, items]) => (
            <div key={category} data-testid={`category-${category.toLowerCase()}`}>
              <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3">{CATEGORY_LABELS[category] || category}</h3>
              <div className="bg-white border border-stone-100 rounded-xl divide-y divide-stone-100">
                {items.map((item) => {
                  const st = item.status || "";
                  const isResolved = st === "har_om_bord" || st === "kjopt";
                  return (
                    <SwipeableRow key={item.id} onDelete={() => removeItem(item.id)} testid={`swipe-${item.id}`}>
                    <div className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 transition-all duration-300 group ${isResolved ? "opacity-70" : ""}`} data-testid={`shopping-list-item-${item.id}`}>
                      {/* Mobile row 1 / Desktop left: status pills + name */}
                      <div className="flex items-start sm:items-center gap-2 flex-1 min-w-0 w-full sm:w-auto">
                        <div className="flex gap-1 flex-shrink-0 mt-0.5 sm:mt-0">
                          <button
                            onClick={() => setItemStatus(item.id, "har_om_bord")}
                            className={`flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition-all border ${
                              st === "har_om_bord"
                                ? "bg-sky-100 text-sky-700 border-sky-300"
                                : "bg-white text-stone-400 border-stone-200 hover:border-sky-300 hover:text-sky-600"
                            }`}
                            title={`Har ${stockLabel}`}
                            data-testid={`status-har-om-bord-${item.id}`}
                          >
                            <Ship className="h-3 w-3" />
                            <span className="hidden sm:inline">{stockLabelTitle}</span>
                          </button>
                          <button
                            onClick={() => setItemStatus(item.id, "kjopt")}
                            className={`flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition-all border ${
                              st === "kjopt"
                                ? "bg-navy-50 text-navy-800 border-seawater-500"
                                : "bg-white text-stone-400 border-stone-200 hover:border-seawater-500 hover:text-seawater-600"
                            }`}
                            title="Kjøpt"
                            data-testid={`status-kjopt-${item.id}`}
                          >
                            <Check className="h-3 w-3" />
                            <span className="hidden sm:inline">Kjøpt</span>
                          </button>
                        </div>
                        {/* Name: wraps on mobile (no truncate), single-line truncated on desktop */}
                        <span className={`text-sm font-medium text-stone-800 flex-1 min-w-0 break-words sm:truncate ${isResolved ? "item-checked" : ""}`}>{item.name}</span>
                        {typeof item.price_live_nok === "number" && (
                          <span
                            className="hidden sm:inline text-xs text-teal-700 font-semibold flex-shrink-0 px-1.5 py-0.5 bg-teal-50 border border-teal-100 rounded-full"
                            title={`${item.price_live_product || item.name} · ${item.price_live_store || "kassal.app"}`}
                            data-testid={`main-live-price-${item.id}`}
                          >
                            {Math.round(item.price_live_nok)} kr
                          </span>
                        )}
                        {item.is_manual && <span className="hidden sm:inline text-xs text-stone-400 bg-stone-50 px-2 py-0.5 rounded-full flex-shrink-0">manuell</span>}
                      </div>
                      {/* Mobile row 2: amount + unit + price + delete (full width, no indent — uses flex space) */}
                      <div className={`flex items-center gap-2 ${isResolved ? "item-checked" : ""}`}>
                        <input
                          type="number"
                          step="0.1"
                          inputMode="decimal"
                          value={item.amount || ""}
                          onChange={(e) => updateItemFieldDebounced(item.id, "amount", e.target.value)}
                          onBlur={(e) => updateItemField(item.id, "amount", e.target.value)}
                          className="w-16 text-xs font-semibold text-stone-600 bg-stone-50 border border-stone-200 rounded px-1.5 py-1 text-center hover:bg-white focus:bg-white focus:border-seawater-600 outline-none"
                          data-testid={`edit-amount-${item.id}`}
                          placeholder="Antall"
                        />
                        <select
                          value={item.unit || "stk"}
                          onChange={(e) => updateItemField(item.id, "unit", e.target.value)}
                          className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded px-1.5 py-1 hover:bg-white focus:bg-white focus:border-seawater-600 outline-none"
                          data-testid={`edit-unit-${item.id}`}
                        >
                          {["stk","kg","g","l","dl","ss","ts","pk","boks","pose","fl"].map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                        {/* Inline category-velger: brukar kan endre kategori
                            for ein vare AI har gjetta feil på. Vi lærer det
                            òg til AI slik at neste gong får same vare same
                            kategori automatisk. */}
                        <select
                          value={CATEGORIES.includes(item.category) ? item.category : "Other"}
                          onChange={async (e) => {
                            const newCat = e.target.value;
                            await updateItemField(item.id, "category", newCat);
                            try {
                              await api.learnShoppingCategory(item.name || "", newCat, "boat");
                              toast.success(`AI vil hugse: «${item.name}» → ${CATEGORY_LABELS[newCat] || newCat}`);
                            } catch (err) {
                              console.warn("Kunne ikkje lære kategori", err?.message);
                            }
                          }}
                          className="text-[10px] text-stone-500 bg-stone-50 border border-stone-200 rounded px-1.5 py-1 hover:bg-white focus:bg-white focus:border-seawater-600 outline-none max-w-[110px]"
                          data-testid={`edit-category-${item.id}`}
                          title="Endre kategori — AI hugsar valet"
                        >
                          {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
                        </select>
                        {typeof item.price_live_nok === "number" && (
                          <span
                            className="sm:hidden text-[11px] text-teal-700 font-semibold flex-shrink-0 px-2 py-0.5 bg-teal-50 border border-teal-100 rounded-full"
                            title={`${item.price_live_product || item.name} · ${item.price_live_store || "kassal.app"}`}
                          >
                            {Math.round(item.price_live_nok)} kr
                          </span>
                        )}
                        {item.is_manual && <span className="sm:hidden text-[10px] text-stone-400 bg-stone-50 px-1.5 py-0.5 rounded-full flex-shrink-0">manuell</span>}
                        <button onClick={() => removeItem(item.id)} className="ml-auto p-1.5 hover:bg-red-50 rounded-md text-stone-300 hover:text-red-500 transition-colors sm:opacity-0 sm:group-hover:opacity-100" data-testid={`remove-item-${item.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    </SwipeableRow>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}
      {/* End of main list tab */}

      {/* Add manual item - sticky bottom */}
      <QuickAddBar
        newItemName={newItemName}
        setNewItemName={setNewItemName}
        suggestionsOpen={suggestionsOpen}
        setSuggestionsOpen={setSuggestionsOpen}
        nameSuggestions={nameSuggestions}
        suggestLoading={suggestLoading}
        onSelectSuggestion={selectSuggestion}
        showAddDetails={showAddDetails}
        setShowAddDetails={setShowAddDetails}
        newItemAmount={newItemAmount}
        setNewItemAmount={setNewItemAmount}
        newItemUnit={newItemUnit}
        setNewItemUnit={setNewItemUnit}
        newItemCategory={newItemCategory}
        setNewItemCategory={setNewItemCategory}
        onQuickAddSubmit={handleQuickAddSubmit}
        onAddManualItem={addManualItem}
        onOpenBarcode={() => setBarcodeOpen(true)}
        onOpenNotes={() => setNotesOpen(true)}
      />

      {/* Add receipt dialog */}
      <AddReceiptDialog
        open={receiptDialogOpen}
        onOpenChange={setReceiptDialogOpen}
        newReceipt={newReceipt}
        setNewReceipt={setNewReceipt}
        store={shoppingList?.store}
        onSave={addReceipt}
      />

      {/* Import from notes dialog */}
      <ImportNotesDialog
        open={notesOpen}
        onOpenChange={setNotesOpen}
        notesText={notesText}
        setNotesText={setNotesText}
        onImport={importFromNotes}
      />

      {/* Generate from meal-plan dialog (meal-type + recipe selection) */}
      <GenerateFromPlanDialog
        open={generateDialogOpen}
        onOpenChange={setGenerateDialogOpen}
        weekStart={weekStart}
        shift="1"
        servings={servings}
        onGenerated={async (sl) => {
          setShoppingList(sl);
          // Auto-fetch live prices for the freshly-generated items so the user
          // sees totalpris straight away. Best-effort — fail silently.
          if (!sl?.items?.length) return;
          const store = selectedStore || activeBoat?.preferred_store || "";
          setRefreshingPrices(true);
          try {
            const updatedItems = await Promise.all(
              sl.items.map(async (it) => {
                if (!it.name || typeof it.price_live_nok === "number") return it;
                try {
                  const res = await api.kassalQuickPrice(it.name, store);
                  if (res.data?.price_nok) {
                    return { ...it, price_live_nok: res.data.price_nok, price_live_store: res.data.store, price_live_product: res.data.product };
                  }
                } catch { /* ignore */ }
                return it;
              }),
            );
            const updated = { ...sl, items: updatedItems };
            setShoppingList(updated);
            await api.updateShoppingList(weekStart, updated, activeListId, activeShift);
            const matched = updatedItems.filter((i) => typeof i.price_live_nok === "number").length;
            const total = updatedItems.length;
            const sum = updatedItems.reduce((s, i) => s + (Number(i.price_live_nok) || 0), 0);
            toast.success(`Auto-priser hentet · ${matched}/${total} varer · ca ${Math.round(sum)} kr`);
          } catch { /* non-fatal */ }
          finally { setRefreshingPrices(false); }
        }}
      />
    </div>
  );
}
